import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useDebounce } from 'ahooks';
import { useActivitySensor } from '@archipro-website/react-hooks';
import { useUserLoggedIn } from '~/modules/user';
import {
    fetchUserActiveStatuses,
    type UserActiveStatus,
} from '../api/fetch-user-active-statuses';
import { RequestRateGate } from '../util/request-rate-gate';
import { useUserActiveStatusUpdates } from './use-user-active-status-updates';

export type { UserActiveStatus };

/** The cached active status for a user. */
interface CachedActiveStatus {
    status: UserActiveStatus;
    lastFetched: number;
}

/**
 * The cache TTL in milliseconds (5 minutes). WebSocket presence pushes keep
 * statuses live in between; polling is only the fallback, so a long TTL keeps
 * request volume low.
 */
const CACHE_TTL_MS = 5 * 60 * 1000;
/**
 * How often to look for stale cache entries to re-fetch.
 */
const STALE_SWEEP_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Limit each request batch so the active-status query string stays reasonably
 * short and per-request backend work stays bounded.
 */
const MAX_MEMBER_IDS_PER_REQUEST = 50;
/**
 * Small grace period when scheduling a retry for the moment the rate gate
 * reopens, so the retry cannot land a few milliseconds too early.
 */
const GATE_REOPEN_GRACE_MS = 50;

/**
 * Rate gate for active-status requests. Module-scoped (like the atoms below) so
 * remounts and render churn can never reset it. Worst case is roughly one
 * request per 5 minutes per tab while requests keep failing.
 */
const fetchGate = new RequestRateGate({
    minIntervalMs: 2 * 1000,
    initialBackoffMs: 5 * 1000,
    maxBackoffMs: 5 * 60 * 1000,
});

/** User status cache by member ID. */
const userStatusCacheAtom = atom<Map<number, CachedActiveStatus>>(new Map());

/**
 * The set of member IDs waiting to be fetched. Components using
 * `useUserActiveStatus` add their member IDs here.
 */
const membersToFetchAtom = atom<Set<number>>(new Set<number>());

/** Member IDs whose fetch is currently in flight. */
const fetchingMemberIDsAtom = atom<Set<number>>(new Set<number>());

/**
 * Hook to get the active status for a single member.
 * This hook registers the member ID to be batch-fetched by useLoadUserStatuses.
 *
 * @param memberIDs The member IDs to get the active status for.
 * @returns The active status ('active' | 'inactive') or `undefined` if not yet loaded.
 */
export function useUserActiveStatus(
    memberIDs: number[] | null | undefined,
): Record<number, UserActiveStatus | null> {
    const memberIDsString = memberIDs ? JSON.stringify(memberIDs) : '';
    const isMounted = useRef(false);
    const isRegistered = useRef(false);
    const cache = useAtomValue(userStatusCacheAtom);
    const setMembersToFetch = useSetAtom(membersToFetchAtom);
    const fetchingIDs = useAtomValue(fetchingMemberIDsAtom);
    const { user, isLoggedIn } = useUserLoggedIn();

    // Register this member ID to be fetched.
    useEffect(() => {
        if (!memberIDs?.length || isRegistered.current) {
            return;
        }
        const now = Date.now();
        const membersToFetch = memberIDs.filter((id) => {
            if (isLoggedIn && id === user.ID) {
                // Don't fetch the user's own status.
                return false;
            }
            const cached = cache.get(id);
            return !cached || now - cached.lastFetched > CACHE_TTL_MS;
        });
        if (membersToFetch.length > 0) {
            setMembersToFetch((prev) => {
                const next = new Set([...prev, ...membersToFetch]);
                if (next.size !== prev.size) {
                    return next;
                }
                return prev;
            });
        }
        isRegistered.current = true;
    }, [memberIDs, isLoggedIn, user.ID, cache, fetchingIDs, setMembersToFetch]);

    // Recheck the statuses when the member IDs change.
    useEffect(() => {
        if (isMounted.current) {
            isRegistered.current = false;
        } else {
            isMounted.current = true;
        }
    }, [memberIDsString]);

    // Return the cached status.
    return useMemo(() => {
        if (!memberIDs?.length) {
            return {};
        }
        return memberIDs.reduce<Record<number, UserActiveStatus | null>>(
            (acc, memberID) => {
                const cached = cache.get(memberID);
                acc[memberID] = cached?.status ?? null;
                return acc;
            },
            {},
        );
    }, [memberIDs, cache]);
}

/**
 * Hook to batch load user active statuses. This should be called **only once** at
 * the root component (`AppPage`) to handle batch fetching of user active statuses.
 *
 * This hook watches the `membersToFetchAtom` and fetches statuses when:
 * - There are members waiting to be fetched.
 * - No request is in flight.
 * - The module-scoped {@link fetchGate} allows another request.
 *
 * Results are cached with a 5-minute TTL.
 */
export function useBatchLoadUserActiveStatuses() {
    const { isLoggedIn } = useUserLoggedIn();
    const isPageActive = useActivitySensor();
    const [membersToFetch, setMembersToFetch] = useAtom(membersToFetchAtom);
    const [fetchingIDs, setFetchingIDs] = useAtom(fetchingMemberIDsAtom);
    const [cache, setCache] = useAtom(userStatusCacheAtom);
    const [isLoading, setIsLoading] = useState(false);
    // Synchronous in-flight guard. `isLoading` state drives effect re-runs, but
    // a ref is needed to block a second request within the same tick before the
    // state update commits.
    const inFlightRef = useRef(false);
    // The in-flight request's abort controller, used to cancel on logout/unmount.
    const abortRef = useRef<AbortController | null>(null);
    // Latest logged-in state, read after an await resolves. The fetchStatuses
    // closure captures isLoggedIn at call time, so a request that resolves just
    // after logout needs the current value to avoid repopulating the cache.
    const isLoggedInRef = useRef(isLoggedIn);
    useEffect(() => {
        isLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn]);
    const memberIDsToFetch = useMemo(() => {
        if (!isLoggedIn) {
            return [];
        }
        return Array.from(membersToFetch).filter((id) => !fetchingIDs.has(id));
    }, [membersToFetch, fetchingIDs, isLoggedIn]);
    const memberIDsToFetchDebounced = useDebounce(memberIDsToFetch, {
        wait: 100,
    });
    const memberIDsBatchToFetch = useMemo(() => {
        return memberIDsToFetchDebounced.slice(0, MAX_MEMBER_IDS_PER_REQUEST);
    }, [memberIDsToFetchDebounced]);

    useUserActiveStatusUpdates({
        onMessage: (message) => {
            if (message.type === 'user/online') {
                setCache((prev) => {
                    const next = new Map(prev);
                    next.set(message.member_id, {
                        status: 'active',
                        lastFetched: Date.now(),
                    });
                    return next;
                });
            } else if (message.type === 'user/offline') {
                setCache((prev) => {
                    const next = new Map(prev);
                    next.set(message.member_id, {
                        status: 'inactive',
                        lastFetched: Date.now(),
                    });
                    return next;
                });
            }
        },
    });

    const fetchStatuses = useCallback(
        async (idsToFetch: number[]) => {
            if (
                idsToFetch.length === 0 ||
                inFlightRef.current ||
                !isLoggedIn ||
                !fetchGate.isOpen()
            ) {
                return;
            }
            // Assume failure until confirmed below, so a transport error that
            // never resolves still throttles the next attempt.
            fetchGate.closePessimistically();
            inFlightRef.current = true;
            setIsLoading(true);
            setFetchingIDs(new Set(idsToFetch));
            setMembersToFetch((prev) => {
                const next = new Set(prev);
                for (const id of idsToFetch) {
                    next.delete(id);
                }
                return next;
            });

            const controller = new AbortController();
            abortRef.current = controller;
            const statuses = await fetchUserActiveStatuses(
                idsToFetch,
                controller.signal,
            );
            abortRef.current = null;
            inFlightRef.current = false;
            setIsLoading(false);

            // Superseded by logout/unmount; those handlers own the atom cleanup.
            // Re-check isLoggedIn (not the captured value) so a response landing
            // just after logout cannot repopulate the cleared cache.
            if (controller.signal.aborted || !isLoggedInRef.current) {
                return;
            }

            if (statuses) {
                fetchGate.recordSuccess();
                const fetchedAt = Date.now();
                setCache((prev) => {
                    const next = new Map(prev);
                    for (const id of idsToFetch) {
                        next.set(id, {
                            status: statuses[id] ?? 'inactive',
                            lastFetched: fetchedAt,
                        });
                    }
                    return next;
                });
            } else {
                fetchGate.recordFailure();
                // Re-queue for retry; the gate (not a cached "offline" value)
                // throttles when it next goes out.
                setMembersToFetch((prev) => new Set([...prev, ...idsToFetch]));
            }
            setFetchingIDs((prev) => (prev.size === 0 ? prev : new Set()));
        },
        [isLoggedIn, setCache, setFetchingIDs, setMembersToFetch],
    );

    // Trigger a batched fetch. When the rate gate is closed, schedule the fetch
    // for the moment it reopens instead of dropping the batch.
    useEffect(() => {
        if (!memberIDsBatchToFetch.length || isLoading) {
            return;
        }
        const gateWaitMs = fetchGate.msUntilOpen();
        if (gateWaitMs <= 0) {
            void fetchStatuses(memberIDsBatchToFetch);
            return;
        }
        const timerId = setTimeout(() => {
            void fetchStatuses(memberIDsBatchToFetch);
        }, gateWaitMs + GATE_REOPEN_GRACE_MS);

        return () => {
            clearTimeout(timerId);
        };
    }, [memberIDsBatchToFetch, isLoading, fetchStatuses]);

    // Periodically check for stale cache entries and re-fetch them.
    useEffect(() => {
        if (!isLoggedIn || !isPageActive) {
            return;
        }
        const intervalId = setInterval(() => {
            const now = Date.now();
            const staleIDs: number[] = [];

            for (const [memberID, cached] of cache.entries()) {
                if (now - cached.lastFetched > CACHE_TTL_MS) {
                    staleIDs.push(memberID);
                }
            }

            if (staleIDs.length > 0) {
                setMembersToFetch((prev) => {
                    const next = new Set([...prev, ...staleIDs]);
                    if (next.size !== prev.size) {
                        return next;
                    }
                    return prev;
                });
            }
        }, STALE_SWEEP_INTERVAL_MS);

        return () => {
            clearInterval(intervalId);
        };
    }, [isLoggedIn, isPageActive, cache, setMembersToFetch]);

    // Abort any in-flight request when this orchestrator unmounts.
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    // Clear cache and reset the gate on logout.
    useEffect(() => {
        if (!isLoggedIn) {
            abortRef.current?.abort();
            abortRef.current = null;
            inFlightRef.current = false;
            fetchGate.reset();
            setIsLoading(false);
            setCache(new Map());
            setFetchingIDs(new Set());
            setMembersToFetch(new Set());
        }
    }, [isLoggedIn, setCache, setFetchingIDs, setMembersToFetch]);

    return {
        /** The cached statuses. */
        statuses: cache,
        /** Whether a status request is currently in flight. */
        isLoading,
    };
}
